import classNames from 'classnames';
import styles from 'components/users/flexlist/FlexList.module.scss';
import memoizeOne from 'memoize-one';
import React, { Component } from 'react';

export const LOADING = 0;
export const PARTIAL = 1;
export const READY = 2;
export const ERROR = -1;
export type LoadingStatus = -1 | 0 | 1 | 2;

export interface CellRenderProps<T> {
  rowIndex: number;
  colIndex: number;
  isVisible: boolean;
  item?: T;
  status: LoadingStatus;
  key: string;
}

export interface HeaderRendererProps {
  key: string;
}

export interface Column<T> {
  name: string;
  header: (props: HeaderRendererProps) => React.ReactElement;
  cell: (props: CellRenderProps<T>) => React.ReactElement;
  hide?: boolean;
}

export interface ItemStatusWrapper<T> {
  status: LoadingStatus;
  item?: T;
}

interface Props<T> {
  columns: Array<Column<T>>;
  data: Array<ItemStatusWrapper<T>>;
  rowClassName?: string;
  headerClassName?: string;
}

/***
 * FlexList, basic list component based on flexbox
 * NOTE: I have a feeling we could solve this with function components etc? but didn't figure out how
 *       to combine FC and generics..
 *       const FlexList: <T>(p: Props<T>) => React.ReactElement<Props<T>> = props => {
 */
class FlexList<T> extends Component<Props<T>> {
  getVisibleColumns = memoizeOne((columns: Array<Column<T>>) => columns.filter(c => !c.hide));

  render() {
    const { columns, data } = this.props;

    const visibleColumns = this.getVisibleColumns(columns);

    return (
      <div className={styles.list}>
        <div className={classNames(styles.headerContainer, this.props.headerClassName)}>
          {visibleColumns.map((col: Column<T>) => {
            return col.header({
              key: col.name,
            });
          })}
        </div>

        {data.map((row, index) => {
          const info = data[index];
          const safeInfo: ItemStatusWrapper<T> = info || { status: LOADING };
          const isLastRow = index + 1 === data.length;

          const rowClass = classNames(styles.rowContainer, this.props.rowClassName, {
            [styles.lastRow]: isLastRow,
          });

          return (
            <div className={rowClass} key={index}>
              {visibleColumns.map((c, colIndex: number) => {
                return c.cell({
                  item: safeInfo.item,
                  rowIndex: index,
                  colIndex,
                  isVisible: !c.hide,
                  status: safeInfo.status,
                  key: c.name,
                });
              })}
            </div>
          );
        })}
      </div>
    );
  }
}

export default FlexList;
